import { ApiService } from '@supply-mover-2-0/core';
import type { ApiResponseDataModel } from '@supply-mover-2-0/shared';

import { CartValidationManager } from './CartValidationManager';

interface CartItem {
	quantity: number;
	price: number | string;
	// Preserved by useCartOperations when it round-trips items through here
	originalPrice?: number | string;
}

// Shape of the cart order payload as this file reads it. Some responses nest
// the order under a further `data` key (see `response.data.data ?? response.data`).
interface CartOrderPayloadModel {
	data?: CartOrderPayloadModel;
	orderDetails?: Array<{
		_fk_item__id?: number;
		quantityOrdered?: number;
		price?: number;
	}>;
}

interface CartStateData {
	items: Record<number, CartItem>;
	pendingPrices: Record<number, number>;
}

type CartListener = (cartItems: Record<number, CartItem>) => void;

class SharedCartState {
	private cartData: Record<string, CartStateData> = {}; // clientId -> cart state data
	private readonly listeners: Map<string, Set<CartListener>> = new Map();

	// Get cart items for a specific client
	getCartItems(clientId: string): Record<number, CartItem> {
		return this.cartData[clientId]?.items ?? {};
	}

	// Update cart items for a specific client
	updateCartItems(clientId: string, items: Record<number, CartItem>) {
		if (this.cartData[clientId] == null) {
			this.cartData[clientId] = {
				items: {},
				pendingPrices: {},
			};
		}

		this.cartData[clientId].items = items;
		this.notifyListeners(clientId);
	}

	// Pending price methods
	setPendingPrice(clientId: string, itemId: number, price: number) {
		if (this.cartData[clientId] == null) {
			this.cartData[clientId] = {
				items: {},
				pendingPrices: {},
			};
		}

		this.cartData[clientId].pendingPrices[itemId] = price;
		// Notify listeners so components re-render with the new pending price
		this.notifyListeners(clientId);
	}

	getPendingPrice(clientId: string, itemId: number): number | undefined {
		return this.cartData[clientId]?.pendingPrices[itemId];
	}

	clearPendingPrice(clientId: string, itemId: number) {
		if (this.cartData[clientId]?.pendingPrices != null) {
			delete this.cartData[clientId].pendingPrices[itemId];
		}
	}

	// Update a single item in the cart
	updateCartItem(clientId: string, itemId: number, item: CartItem | null) {
		if (this.cartData[clientId] == null) {
			this.cartData[clientId] = {
				items: {},
				pendingPrices: {},
			};
		}

		if (item === null) {
			delete this.cartData[clientId].items[itemId];
		} else {
			this.cartData[clientId].items[itemId] = item;
		}

		this.notifyListeners(clientId);
	}

	// Subscribe to cart changes for a specific client
	subscribe(clientId: string, listener: CartListener): () => void {
		if (!this.listeners.has(clientId)) {
			this.listeners.set(clientId, new Set());
		}

		this.listeners.get(clientId)!.add(listener);

		// Immediately call listener with current state
		listener(this.getCartItems(clientId));

		// Return unsubscribe function
		return () => {
			const clientListeners = this.listeners.get(clientId);

			if (clientListeners != null) {
				clientListeners.delete(listener);

				if (clientListeners.size === 0) {
					this.listeners.delete(clientId);
				}
			}
		};
	}

	// Load cart from server
	async loadCart(clientId: string): Promise<void> {
		try {
			const response = await ApiService.apiGet<
				ApiResponseDataModel<CartOrderPayloadModel>
			>(`/orders/cart/${clientId}`);

			if (response.success && response.data != null) {
				const cartData: Record<number, CartItem> = {};

				// Get the actual cart data (nested under response.data.data)
				const cartResponse = response.data.data ?? response.data;

				// Convert order details to cart items map
				if (
					cartResponse.orderDetails != null &&
					Array.isArray(cartResponse.orderDetails)
				) {
					cartResponse.orderDetails.forEach(
						(detail: {
							_fk_item__id?: number;
							quantityOrdered?: number;
							price?: number;
						}) => {
							if (detail._fk_item__id != null) {
								cartData[detail._fk_item__id] = {
									quantity: detail.quantityOrdered ?? 0,
									price: detail.price ?? 0,
								};
							}
						},
					);
				}

				this.updateCartItems(clientId, cartData);
			} else {
				this.updateCartItems(clientId, {});
			}
		} catch (err) {
			// Don't update state on error, keep existing cart items
		}
	}

	// Clear cart for a client
	clearCart(clientId: string) {
		if (this.cartData[clientId] == null) {
			this.cartData[clientId] = {
				items: {},
				pendingPrices: {},
			};
		} else {
			this.cartData[clientId].items = {};
		}

		// Clear CartValidationManager state to remove any cached validation states
		CartValidationManager.cleanup();

		this.notifyListeners(clientId);
	}

	private notifyListeners(clientId: string) {
		const clientListeners = this.listeners.get(clientId);

		if (clientListeners != null) {
			const cartItems = this.getCartItems(clientId);

			clientListeners.forEach((listener) => {
				try {
					listener(cartItems);
				} catch (error) {
					// Error: SharedCartState Error in cart listener
				}
			});
		}
	}
}

// Export singleton instance
export const sharedCartState = new SharedCartState();
